import React, { useEffect, useMemo, useRef, useState } from 'react';
import './IntegrAI.css';
import { askIntegrAI } from '../../services/integraiApi';
import {
  detectIntegrAIInputLanguage,
  getIntegrAIDebugEnabled,
  getIntegrAITextDirection,
  normalizeIntegrAILanguageCode,
} from '../../services/integraiLanguage';
import {
  buildIntegrAIRequestId,
  trackIntegrAIAnswer,
  trackIntegrAIAsk,
  trackIntegrAIError,
} from '../../lib/analytics/integraiAnalytics';
import { isAnalyticsDebugEnabled } from '../../services/analytics';
import { normalizeSafeUrl } from '../../utils/safeUrl';
import integraiWordmark from '../../photos/integrai/integrai beyaz.svg';

function prettySourceLabel(source) {
  if (source?.source_type === 'live_data') return 'Live (TBA)';
  if (source?.source_type === 'frc_pdf' || source?.source_type === 'wpilib_pdf' || source?.source_type === 'xrp_pdf') return 'PDF';
  if (source?.source_type === 'first_web') return 'Web';
  return source?.source_type || 'Source';
}

const SUGGESTIONS = [
  'What is the 2026 robot weight limit?',
  'Who won SF 1 M1 at 2025 Marmara Regional and what was the score?',
  'Explain bumper rules and dimensions.',
  'Summarize Team 3646 awards since 2016.',
];

const CAPABILITIES = [
  {
    number: '01',
    title: 'Rules & Rulings',
    text: 'Quickly surface the relevant rule, plus context, without digging through PDFs.',
  },
  {
    number: '02',
    title: 'Live Event Insight',
    text: 'Ask about match results, rankings, and awards pulled from The Blue Alliance.',
  },
  {
    number: '03',
    title: 'Team History',
    text: 'Review awards, banners, and milestones for Team 3646 across seasons.',
  },
];

const SOURCE_HIERARCHY = [
  'Official FIRST material',
  'Public technical resources',
  'Live event data from The Blue Alliance',
  'Documented Team 3646 history',
];

function getLatencyMs(startedAt) {
  const finishedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return Math.max(0, Math.round(finishedAt - startedAt));
}

function debugLog(message, details = {}) {
  if (!isAnalyticsDebugEnabled() || typeof console === 'undefined') {
    return;
  }

  console.info('[integrai]', {
    message,
    ...details,
  });
}

function IntegrAI() {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [debugInfo, setDebugInfo] = useState(null);
  const [answerLanguage, setAnswerLanguage] = useState('unknown');
  const debugEnabled = useMemo(() => getIntegrAIDebugEnabled(), []);

  const hasAnswer = useMemo(() => Boolean(answer && answer.trim()), [answer]);
  const inputLanguage = useMemo(() => detectIntegrAIInputLanguage(question), [question]);
  const inputDirection = useMemo(() => getIntegrAITextDirection(question, inputLanguage), [question, inputLanguage]);
  const answerDirection = useMemo(() => getIntegrAITextDirection(answer, answerLanguage), [answer, answerLanguage]);
  const activeRequestRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const onAsk = async (event) => {
    event.preventDefault();

    const rawQuestion = question;
    const trimmed = rawQuestion.trim();
    const pagePath = window.location?.pathname || '/integrai';
    const langHint = detectIntegrAIInputLanguage(rawQuestion);

    if (!trimmed) {
      setValidationError('Please enter a question before submitting.');
      debugLog('Empty IntegrAI submit blocked', {
        pagePath,
        langHint,
      });
      return;
    }

    if (activeRequestRef.current) {
      debugLog('Duplicate IntegrAI submit blocked', {
        requestId: activeRequestRef.current,
        pagePath,
      });
      return;
    }

    const requestId = buildIntegrAIRequestId();
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const controller = new AbortController();

    activeRequestRef.current = requestId;
    abortControllerRef.current = controller;

    setLoading(true);
    setValidationError('');
    setError('');
    setAnswer('');
    setSources([]);
    setDebugInfo(null);
    setAnswerLanguage('unknown');

    void trackIntegrAIAsk({
      requestId,
      pagePath,
      langHint,
      queryLength: trimmed.length,
      isShortQuery: trimmed.length <= 20,
    });

    try {
      const data = await askIntegrAI({
        question: rawQuestion,
        userLang: langHint,
        signal: controller.signal,
      });

      const nextAnswer = typeof data?.answer === 'string' ? data.answer : '';
      const renderedAnswer = nextAnswer || 'No answer returned.';
      const nextSources = Array.isArray(data?.sources) ? data.sources : [];
      const routeFamily = typeof data?.inferred?.route === 'string' ? data.inferred.route : undefined;
      const nextDebugInfo = data?._uiVerification || null;
      const nextAnswerLanguage = normalizeIntegrAILanguageCode(
        data?.response_contract?.requested_language
        || data?.language?.user_language
        || nextDebugInfo?.detectedInputLanguage
        || langHint
      );

      if (isMountedRef.current) {
        setAnswer(renderedAnswer);
        setSources(nextSources);
        setDebugInfo(nextDebugInfo);
        setAnswerLanguage(nextAnswerLanguage);
      }

      debugLog('IntegrAI response verified', nextDebugInfo || {});

      void trackIntegrAIAnswer({
        requestId,
        pagePath,
        langHint,
        latencyMs: getLatencyMs(startedAt),
        httpStatus: data?._httpStatus || 200,
        answerLengthChars: renderedAnswer.length,
        citedSourcesCount: nextSources.length,
        routeFamily,
      });
    } catch (err) {
      const message = err?.message || 'Unable to reach the IntegrAI backend.';

      if (isMountedRef.current) {
        setError(message);
      }

      void trackIntegrAIError({
        requestId,
        pagePath,
        langHint,
        latencyMs: getLatencyMs(startedAt),
        httpStatus: Number.isInteger(err?.httpStatus) ? err.httpStatus : undefined,
        errorType: err?.errorType || 'unknown',
        errorStage: err?.stage || 'unknown',
      });
    } finally {
      if (activeRequestRef.current === requestId) {
        activeRequestRef.current = null;
      }

      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }

      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <article className="integrai-page">
      <header className="integrai-hero fade-in">
        <div className="integrai-hero-content">
          <p className="integrai-kicker">FRC rules, events, and Team 3646 context</p>
          <h1 className="integrai-title">
            <img
              src={integraiWordmark}
              alt=""
              className="integrai-title-image"
              aria-hidden="true"
            />
            <span className="integrai-title-text">IntegrAI</span>
          </h1>
          <p className="integrai-hero-copy">
            IntegrAI blends rulebook context, historical data, and live event information into a single
            assistant. Ask questions in natural language and get structured,
            source-backed answers.
          </p>
          <div className="integrai-hero-actions">
            <a className="integrai-hero-button integrai-hero-button-primary" href="#integrai-chat">Start Asking</a>
            <a className="integrai-hero-link" href="#integrai-source-standards">How sources work</a>
          </div>
        </div>
      </header>

      <section className="integrai-section integrai-chat-section fade-in" id="integrai-chat">
        <div className="integrai-section-header">
          <h2 className="integrai-chat-heading">Ask the real assistant</h2>
          <p>
            The tool is the center of the page: enter a rules, event, robot software, or Team 3646 question and review
            the returned answer with citations when available.
          </p>
        </div>

        <div className="integrai-chat-shell">
          <div className="integrai-chat-context" role="note" aria-label="About IntegrAI">
            <div>
              <p className="integrai-context-label">Built for FRC use</p>
              <p>
                <strong>IntegrAI</strong> answers questions about rules, robot software, and live event data
                (matches, rankings, awards) via The Blue Alliance. Off-topic questions are refused.
              </p>
            </div>

            <div className="integrai-suggestions" aria-label="Suggested questions">
              <p className="integrai-context-label">Suggested questions</p>
              {SUGGESTIONS.map((text) => (
                <button
                  key={text}
                  type="button"
                  className="integrai-suggestion"
                  onClick={() => {
                    setQuestion(text);
                    setValidationError('');
                  }}
                >
                  {text}
                </button>
              ))}
            </div>
          </div>

          <div className="integrai-chat-workspace">
            <form className="integrai-form" onSubmit={onAsk}>
              <label className="integrai-label" htmlFor="integrai-question">
                Your question
              </label>
              <textarea
                id="integrai-question"
                className="integrai-input"
                data-testid="integrai-question-input"
                value={question}
                lang={inputLanguage !== 'unknown' ? inputLanguage : undefined}
                dir={inputDirection}
                placeholder="Ask about rules, events, or team history..."
                onChange={(event) => {
                  setQuestion(event.target.value);
                  if (validationError && event.target.value.trim()) {
                    setValidationError('');
                  }
                }}
                rows={4}
              />
              <div className="integrai-actions">
                <button
                  className="integrai-submit"
                  data-testid="integrai-submit-button"
                  type="submit"
                  disabled={loading}
                  aria-disabled={loading}
                >
                  {loading ? 'Asking...' : 'Ask IntegrAI'}
                </button>
                <span className="integrai-status" aria-live="polite">
                  {loading ? 'Retrieving sources and composing an answer.' : 'Responses include citations when available.'}
                </span>
              </div>
            </form>

            {validationError ? <div className="integrai-validation" role="alert">{validationError}</div> : null}
            {error ? <div className="integrai-error" data-testid="integrai-error" role="alert">{error}</div> : null}

            {hasAnswer ? (
              <div className="integrai-answer" data-testid="integrai-answer">
                <div className="integrai-answer-title">Answer</div>
                <div
                  className="integrai-answer-body"
                  data-testid="integrai-answer-body"
                  lang={answerLanguage !== 'unknown' ? answerLanguage : undefined}
                  dir={answerDirection}
                >
                  {answer}
                </div>
              </div>
            ) : null}

            {debugEnabled && debugInfo ? (
              <details className="integrai-debug" open>
                <summary>Debug Route Trace</summary>
                <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
              </details>
            ) : null}

            {sources.length ? (
              <div className="integrai-sources" data-testid="integrai-source-section">
                <div className="integrai-answer-title">Sources</div>
                <div className="integrai-source-list" data-testid="integrai-source-list">
                  {sources.map((source, idx) => {
                    const safeUrl = source?.url ? normalizeSafeUrl(source.url, { allowRelative: false }) : '';
                    const sourceTitle = source?.title || source?.source || 'Source';

                    return (
                      <div className="integrai-source" data-testid="integrai-source" key={`${source?.source || 'src'}-${idx}`}>
                        <div className="integrai-source-top">
                          <span className="integrai-pill">{prettySourceLabel(source)}</span>
                          <span className="integrai-source-text" dir="auto">
                            {sourceTitle}
                            {source?.page !== null && source?.page !== undefined ? ` - page ${source.page}` : ''}
                          </span>
                        </div>
                        {safeUrl ? (
                          <a
                            className="integrai-source-link"
                            href={safeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {source.url}
                          </a>
                        ) : null}
                        {source?.supports ? (
                          <div className="integrai-source-support" dir="auto">{source.supports}</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="integrai-section integrai-capabilities fade-in">
        <div className="integrai-section-header integrai-section-header-left">
          <h2>What can it do</h2>
        </div>
        <div className="integrai-capability-list">
          {CAPABILITIES.map((capability) => (
            <section className="integrai-capability" key={capability.title} aria-labelledby={`integrai-capability-${capability.number}`}>
              <span className="integrai-capability-number">{capability.number}</span>
              <h3 id={`integrai-capability-${capability.number}`}>{capability.title}</h3>
              <p>{capability.text}</p>
            </section>
          ))}
        </div>
      </section>

      <section className="integrai-section integrai-source-standards fade-in" id="integrai-source-standards">
        <div className="integrai-source-panel">
          <div className="integrai-section-header integrai-section-header-left">
            <h2>Source standards</h2>
            <p>
              IntegrAI prioritizes relevant sources and includes citations when available. Users should verify
              time-sensitive rules against current official FIRST materials.
            </p>
          </div>
          <ol className="integrai-source-hierarchy">
            {SOURCE_HIERARCHY.map((item) => (
              <li key={item}>
                <span>{item}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </article>
  );
}

export default IntegrAI;
